import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { BrandMark } from '@kainos/ui';
import { useAuth } from './auth-context.js';

/**
 * Gate in front of every screen that needs an organization.
 *
 * The three auth states map to three outcomes and no others: still asking
 * Cognito, send them to sign in, or render the app. Guarding at the router
 * root means no page has to check, and none can forget to.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'checking') return <AuthSplash />;

  if (status === 'anonymous') {
    // Remember where they were headed so signing in resumes it, deep link
    // and query string included.
    return (
      <Navigate to="/signin" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  return <>{children}</>;
}

/**
 * Shown only while the session is being restored — usually a few hundred
 * milliseconds. It is the mark alone rather than a page skeleton, because at
 * this point we do not yet know which page is about to be drawn.
 */
function AuthSplash() {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-canvas"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">Verificando tu sesión</span>
      <BrandMark size={44} className="animate-kainos-pulse" />
    </div>
  );
}
